/**
 * Tokens como CSS custom properties.
 *
 * O protótipo aprovado troca tema por `data-theme` no elemento raiz, com as
 * cores em custom properties. A PWA usa exatamente o mesmo mecanismo — não é
 * coincidência conveniente, é o que permite reaproveitar valores do protótipo
 * mecanicamente em vez de traduzi-los.
 *
 * Os nomes das variáveis são os MESMOS do protótipo (`--bg`, `--surf`,
 * `--amber`…), para que um valor copiado de lá continue significando a mesma
 * coisa aqui.
 */

import {
  accent,
  colors,
  dayPalette,
  familyPalette,
  labelTone,
  onAccent,
  onFamily,
  overlay,
  statusOnChipDark,
  type ThemeName,
} from './colors.js';
import { a11y, border, layout, motion, radius, shadow } from './layout.js';
import { fontStack } from './typography.js';

const sombra = (s: { offsetY: number; blur: number; spread?: number; color: string }) =>
  `0 ${s.offsetY}px ${s.blur}px${s.spread ? ` ${s.spread}px` : ''} ${s.color}`;

/** `brandTint2` → `brand-tint-2`, `fieldBorder` → `field-border`. */
const kebab = (nome: string) => nome.replace(/([a-z])([A-Z0-9])/g, '$1-$2').toLowerCase();

/** Variáveis que NÃO mudam entre temas: acentos, tintas, medidas. */
function invariantVars(): Record<string, string> {
  const vars: Record<string, string> = {};

  for (const [name, value] of Object.entries(accent)) vars[`--${name}`] = value;
  for (const [name, value] of Object.entries(onAccent)) vars[`--on-${name}`] = value;

  // Família N (base 1) e a tinta que vale sobre qualquer uma delas.
  familyPalette.forEach((cor, i) => {
    vars[`--family-${i + 1}`] = cor;
  });
  vars['--on-family'] = onFamily;

  for (const [nome, cor] of Object.entries(dayPalette)) vars[`--day-${nome}`] = cor;

  for (const [tom, par] of Object.entries(labelTone)) {
    vars[`--etiqueta-${tom}-bg`] = par.bg;
    vars[`--etiqueta-${tom}-fg`] = par.fg;
  }

  vars['--overlay'] = overlay;

  vars['--font-display'] = fontStack.display;
  vars['--font-ui'] = fontStack.ui;
  vars['--font-mono'] = fontStack.mono;
  vars['--font-code'] = fontStack.code;

  vars['--radius-hero'] = `${radius.hero}px`;
  vars['--radius-card'] = `${radius.card}px`;
  vars['--radius-control'] = `${radius.control}px`;
  vars['--radius-tile'] = `${radius.tile}px`;
  vars['--radius-chip'] = `${radius.chip}px`;

  vars['--border-hairline'] = `${border.hairline}px`;
  vars['--border-emphasis'] = `${border.emphasis}px`;

  vars['--screen-padding'] = `${layout.screenPadding}px`;
  vars['--block-gap'] = `${layout.blockGap}px`;
  vars['--section-gap'] = `${layout.sectionGap}px`;
  vars['--label-gap'] = `${layout.labelGap}px`;
  vars['--header-gap'] = `${layout.headerGap}px`;
  vars['--header-top'] = `${layout.headerTop}px`;
  // Safe area do aparelho (iOS com viewport-fit=cover; 0 onde não há).
  // Só o shell consome: `.app-scroll`, barra inferior e folhas.
  vars['--safe-top'] = 'env(safe-area-inset-top, 0px)';
  vars['--safe-right'] = 'env(safe-area-inset-right, 0px)';
  vars['--safe-bottom'] = 'env(safe-area-inset-bottom, 0px)';
  vars['--safe-left'] = 'env(safe-area-inset-left, 0px)';
  vars['--min-target'] = `${a11y.minTarget}px`;

  vars['--shadow-e0'] = 'none';
  vars['--shadow-raised'] = sombra(shadow.raised);
  vars['--shadow-e1'] = sombra(shadow.e1);
  vars['--shadow-e2'] = sombra(shadow.e2);
  vars['--shadow-brand'] = sombra(shadow.brand);

  vars['--press-scale'] = String(motion.pressScale);
  vars['--press-scale-card'] = String(motion.pressScaleCard);
  vars['--press-scale-fab'] = String(motion.pressScaleFab);
  vars['--duration-press'] = `${motion.pressMs}ms`;
  vars['--duration-screen'] = `${motion.screenEnterMs}ms`;
  vars['--duration-tab'] = `${motion.tabMs}ms`;
  vars['--duration-sheet'] = `${motion.sheetMs}ms`;
  vars['--duration-scrim'] = `${motion.scrimMs}ms`;
  vars['--duration-check-fill'] = `${motion.checkFillMs}ms`;
  vars['--duration-check-stroke'] = `${motion.checkStrokeMs}ms`;
  vars['--duration-progress'] = `${motion.progressMs}ms`;
  vars['--duration-pop'] = `${motion.popMs}ms`;
  vars['--duration-toast'] = `${motion.toastEnterMs}ms`;
  vars['--duration-float'] = `${motion.floatMs}ms`;
  vars['--duration-shimmer'] = `${motion.shimmerMs}ms`;
  vars['--duration-pulse'] = `${motion.pulseMs}ms`;
  vars['--stagger'] = `${motion.staggerMs}ms`;
  vars['--easing'] = motion.easing;
  vars['--easing-bounce'] = motion.easingBounce;

  return vars;
}

/** Variáveis neutras de um tema. */
export function themeVars(theme: ThemeName): Record<string, string> {
  const palette = colors[theme];
  return {
    '--bg': palette.bg,
    '--surf': palette.surf,
    '--surf2': palette.surf2,
    '--line': palette.line,
    '--ink': palette.ink,
    '--mut': palette.mut,
    '--chip': palette.chip,
    '--ink-2': palette.ink2,
    '--disabled-fg': palette.disabled,
    '--track': palette.track,
    '--field-border': palette.fieldBorder,
    '--link': palette.link,
    // Pares tint/ink dos acentos: --brand-tint, --success-ink, --warn-tint…
    ...Object.fromEntries(Object.entries(palette.tone).map(([nome, cor]) => [`--${kebab(nome)}`, cor])),
    // Texto de status sobre --chip: no Dark o acento sólido não alcança AA.
    '--status-success': theme === 'dark' ? statusOnChipDark.success : accent.success,
    '--status-danger': theme === 'dark' ? statusOnChipDark.danger : accent.danger,
  };
}

function block(selector: string, vars: Record<string, string>): string {
  const body = Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
  return `${selector} {\n${body}\n}`;
}

/**
 * Folha de estilo completa dos tokens.
 *
 * O tema escuro é o padrão em `:root` — o design nasceu escuro. `[data-theme]`
 * sobrepõe, e é o que o `ThemeProvider` da PWA alterna.
 */
export function tokensStylesheet(): string {
  return [
    block(':root', { ...invariantVars(), ...themeVars('dark') }),
    block(':root[data-theme="light"]', themeVars('light')),
    block(':root[data-theme="dark"]', themeVars('dark')),
  ].join('\n\n');
}
